import { useLearningDisplayItem } from "../exercises/LearningDisplayBoundary";
import { useRecommendationRunStarter } from "../exercises/recommendation-context";
import { useMemo, useState, useEffect } from "react";
import type { StellenwertAdditionExerciseConfig, StellenwertAdditionPhase, StellenwertAdditionRunSettings } from "@calega/content";
import type { StellenwertAdditionRun } from "@calega/exercise-engine";
import type { Account } from "../lib/types";
import { useRealtime } from "../realtime/RealtimeContext";
import { ExerciseMenu } from "./ExerciseMenu";
import { ExerciseWaitingCard } from "./ExerciseWaitingCard";
import { abortExerciseRun, submitExerciseAnswer } from "../exercises/learning-api";
import { CompletedRunActions } from "./CompletedRunActions";
import { CompletedRunHistory } from "./CompletedRunHistory";
import { RunCompletion, useRunCompletion } from "./RunCompletion";

type ExercisePhase = "transcribe" | "solve" | "feedback" | "complete";

interface ItemDraft {
  writtenFirstAddend: string;
  writtenSecondAddend: string;
  firstIncorrectWrittenFirstAddend: number | null;
  firstIncorrectWrittenSecondAddend: number | null;
  transcriptionCorrectionCount: number;
  intermediateEntered: string;
  resultEntered: string;
  movedTens: number;
  transcriptionNeedsReview: boolean;
}

interface StoredAnswer {
  itemId: string;
  intermediateEntered: number;
  resultEntered: number;
  correct: boolean;
}

interface StellenwertAdditionState {
  runId: string;
  run: StellenwertAdditionRun;
  index: number;
  phase: ExercisePhase;
  draft: ItemDraft;
  answers: StoredAnswer[];
  result: { score: number; maxScore: number } | null;
}

const phaseOptions: Array<{ id: StellenwertAdditionPhase; label: string; description: string }> = [
  { id: "move_tens", label: "Zehner bewegen", description: "Die Zehnerstangen der Lieferung werden ins Lager bewegt." },
  { id: "observe_material", label: "Material anschauen", description: "Das Ausgangsmaterial bleibt liegen und unterstützt die Vorstellung." },
  { id: "mental_short", label: "Kurz im Kopf", description: "Das Material bleibt liegen; die Sprechhilfe ist verkürzt." },
];

function emptyDraft(): ItemDraft {
  return {
    writtenFirstAddend: "",
    writtenSecondAddend: "",
    firstIncorrectWrittenFirstAddend: null,
    firstIncorrectWrittenSecondAddend: null,
    transcriptionCorrectionCount: 0,
    intermediateEntered: "",
    resultEntered: "",
    movedTens: 0,
    transcriptionNeedsReview: false,
  };
}

function Cubes({ count }: { count: number }) {
  return <div className="place-value-cubes" aria-hidden="true">
    {Array.from({ length: count }, (_, index) => <span className="place-value-cube" key={index} />)}
  </div>;
}

function Rods({ count, movable, onMove }: { count: number; movable: boolean; onMove?: () => void }) {
  return <div className="place-value-rods" aria-hidden={!movable}>
    {Array.from({ length: count }, (_, index) => movable
      ? <button
          type="button"
          className="place-value-rod movable"
          aria-label={`Zehnerstange ${index + 1} ins Lager bewegen`}
          onClick={onMove}
          key={index}
        />
      : <span className="place-value-rod" key={index} />)}
  </div>;
}

function MaterialScene({ state, canAnswer, moveRod }: { state: StellenwertAdditionState; canAnswer: boolean; moveRod: () => void }) {
  const item = state.run.items[state.index]!;
  const movable = state.run.settings.phase === "move_tens" && state.phase !== "feedback" && canAnswer;
  const warehouseTens = Math.floor(item.firstAddend / 10) + state.draft.movedTens;
  const truckTens = item.tensPart / 10 - state.draft.movedTens;
  return <div className="place-value-scene">
    <section className="place-value-location warehouse" aria-label={`Im Lager sind ${item.firstAddend} Paletten`}>
      <div className="place-value-building" aria-hidden="true"><span>Lager</span></div>
      <div className="place-value-material">
        <Rods count={warehouseTens} movable={false} />
        <Cubes count={item.firstAddend % 10} />
      </div>
    </section>
    <section className="place-value-location truck" aria-label={`Der Lieferwagen bringt ${item.secondAddend} Paletten`}>
      <div className="place-value-truck" aria-hidden="true"><span>Lieferung</span><i /><i /></div>
      <div className="place-value-material">
        <Rods count={truckTens} movable={movable} onMove={moveRod} />
        <Cubes count={item.onesPart} />
      </div>
    </section>
  </div>;
}

function SpeechHelp({ state }: { state: StellenwertAdditionState }) {
  const item = state.run.items[state.index]!;
  const reveal = state.phase === "feedback";
  const intermediate = reveal ? item.intermediate : state.draft.intermediateEntered || "___";
  const result = reveal ? item.result : state.draft.resultEntered || "___";
  if (state.run.settings.phase === "mental_short") {
    return <p className="speech-help"><strong>Sprechhilfe:</strong> „{item.firstAddend} plus {item.tensPart} sind {intermediate}, plus {item.onesPart} sind {result}.“</p>;
  }
  return <p className="speech-help"><strong>Sprechhilfe:</strong> „Im Lager sind {item.firstAddend}. Zuerst kommen {item.tensPart} dazu, dann sind es {intermediate}. Dann noch {item.onesPart}, dann sind es {result}.“</p>;
}

export function StellenwertAddition({ account, config, pageId }: { account: Account; config: StellenwertAdditionExerciseConfig; pageId: string }) {
  const startExerciseRun = useRecommendationRunStarter();
  const completion = useRunCompletion();
  const realtime = useRealtime();
  const facilitator = account.roles.some((role) => role === "ADMIN" || role === "ORGANIZATION" || role === "THERAPIST");
  const follower = Boolean(realtime.session && !facilitator);
  const canControl = !realtime.session || facilitator;
  const canAnswer = !realtime.session || facilitator || follower;
  const [phase, setPhase] = useState<StellenwertAdditionPhase>(config.defaultPhase);
  const [count, setCount] = useState<5 | 10>(config.defaultCount);
  const [exercise, setExercise] = useState<StellenwertAdditionState | null>(null);
  useLearningDisplayItem(exercise?.runId, exercise?.index ?? 0, Boolean(exercise && exercise.phase !== "complete"));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const score = useMemo(() => exercise?.answers.filter(({ correct }) => correct).length ?? 0, [exercise?.answers]);

  function publish(state: StellenwertAdditionState | null) {
    realtime.publishExercise({ exerciseType: "stellenwert-addition", pageId, state });
  }

  function commit(next: StellenwertAdditionState) {
    setExercise(next);
    publish(next);
  }

  useEffect(() => {
    const synced = realtime.state.exercise as { exerciseType?: string; pageId?: string; state?: StellenwertAdditionState | null } | null;
    if (synced?.exerciseType === "stellenwert-addition" && synced.pageId === pageId) {
      setExercise(synced.state ?? null);
      setMessage("");
    }
  }, [realtime.state.exercise, pageId]);

  async function start(runSettings: StellenwertAdditionRunSettings = { phase, count }) {
    if (busy || !canControl) return;
    setBusy(true);
    try {
      const response = await startExerciseRun<{ runId: string; exercise: StellenwertAdditionRun }>({ contentPageId: pageId, settings: runSettings });
      const next: StellenwertAdditionState = {
        runId: response.runId,
        run: response.exercise,
        index: 0,
        phase: "transcribe",
        draft: emptyDraft(),
        answers: [],
        result: null,
      };
      commit(next);
      setMessage("");
    } finally {
      setBusy(false);
    }
  }

  async function backToSettings() {
    if (!exercise || busy || !canControl) return;
    setBusy(true);
    try {
      if (exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
      setPhase(exercise.run.settings.phase);
      setCount(exercise.run.settings.count);
      setExercise(null);
      publish(null);
      setMessage("");
    } finally {
      setBusy(false);
    }
  }

  async function remix() {
    if (!exercise || busy || !canControl) return;
    const settings = exercise.run.settings;
    setBusy(true);
    try {
      if (exercise.phase !== "complete") await abortExerciseRun(exercise.runId);
    } finally {
      setBusy(false);
    }
    await start(settings);
  }

  function updateDraft(changes: Partial<ItemDraft>) {
    if (!exercise || exercise.phase === "feedback" || exercise.phase === "complete" || !canAnswer) return;
    commit({ ...exercise, draft: { ...exercise.draft, ...changes } });
  }

  function moveRod() {
    if (!exercise || exercise.run.settings.phase !== "move_tens") return;
    const item = exercise.run.items[exercise.index];
    if (!item || exercise.draft.movedTens >= item.tensPart / 10) return;
    updateDraft({ movedTens: exercise.draft.movedTens + 1 });
  }

  function checkTranscription() {
    if (!exercise || exercise.phase !== "transcribe" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    const first = Number(exercise.draft.writtenFirstAddend);
    const second = Number(exercise.draft.writtenSecondAddend);
    if (!item || !Number.isInteger(first) || !Number.isInteger(second) || !exercise.draft.writtenFirstAddend || !exercise.draft.writtenSecondAddend) {
      setMessage("Trage beide Mengen aus der Sachsituation ein.");
      return;
    }
    if (first !== item.firstAddend || second !== item.secondAddend) {
      const firstWrongAttemptMissing = exercise.draft.firstIncorrectWrittenFirstAddend === null;
      commit({
        ...exercise,
        draft: {
          ...exercise.draft,
          firstIncorrectWrittenFirstAddend: firstWrongAttemptMissing ? first : exercise.draft.firstIncorrectWrittenFirstAddend,
          firstIncorrectWrittenSecondAddend: firstWrongAttemptMissing ? second : exercise.draft.firstIncorrectWrittenSecondAddend,
          transcriptionCorrectionCount: exercise.draft.transcriptionCorrectionCount + 1,
          transcriptionNeedsReview: true,
        },
      });
      setMessage("");
      return;
    }
    commit({ ...exercise, phase: "solve", draft: { ...exercise.draft, transcriptionNeedsReview: false } });
    setMessage("");
  }

  async function checkSolution() {
    if (!exercise || exercise.phase !== "solve" || busy || !canAnswer) return;
    const item = exercise.run.items[exercise.index];
    const intermediateEntered = Number(exercise.draft.intermediateEntered);
    const resultEntered = Number(exercise.draft.resultEntered);
    if (!item || !exercise.draft.intermediateEntered || !exercise.draft.resultEntered || !Number.isInteger(intermediateEntered) || !Number.isInteger(resultEntered)) {
      setMessage("Trage Zwischen- und Endergebnis ein.");
      return;
    }
    setBusy(true);
    try {
      const correct = intermediateEntered === item.intermediate && resultEntered === item.result;
      await submitExerciseAnswer(exercise.runId, {
          type: "stellenwert-addition",
          itemId: item.id,
          writtenFirstAddend: Number(exercise.draft.writtenFirstAddend),
          writtenSecondAddend: Number(exercise.draft.writtenSecondAddend),
          firstIncorrectWrittenFirstAddend: exercise.draft.firstIncorrectWrittenFirstAddend,
          firstIncorrectWrittenSecondAddend: exercise.draft.firstIncorrectWrittenSecondAddend,
          transcriptionCorrectionCount: exercise.draft.transcriptionCorrectionCount,
          intermediateEntered,
          resultEntered,
        });
      commit({
        ...exercise,
        phase: "feedback",
        answers: [...exercise.answers, { itemId: item.id, intermediateEntered, resultEntered, correct }],
      });
      setMessage("");
    } finally {
      setBusy(false);
    }
  }

  async function next() {
    if (!exercise || exercise.phase !== "feedback" || busy || !canControl) return;
    if (exercise.index + 1 >= exercise.run.items.length) {
      await completion.complete(exercise.runId, (result) => commit({ ...exercise, result, phase: "complete" }));
      return;
    }
    commit({ ...exercise, index: exercise.index + 1, phase: "transcribe", draft: emptyDraft() });
    setMessage("");
  }

  async function closeCompletedRun(repeat: boolean) {
    if (!exercise || exercise.phase !== "complete") return;
    if (repeat) await remix();
    else await backToSettings();
  }

  if (completion.status.phase !== "idle") return <RunCompletion completion={completion} />;

  if (follower && !exercise) {
    return <ExerciseWaitingCard className="stellenwert-addition" />;
  }

  if (!exercise) {
    return <section className="stellenwert-addition">
      <p className="quiz-instructions learning-text">{config.instructions}</p>
      <div className="mathe-settings stellenwert-settings">
        <fieldset>
          <legend>Lernphase</legend>
          <div className="stellenwert-phase-options">
            {phaseOptions.map((option) => <label className="checkbox-label" key={option.id}>
              <input type="radio" name={`${pageId}-stellenwert-phase`} checked={phase === option.id} onChange={() => setPhase(option.id)} />
              <span><strong>{option.label}</strong><small>{option.description}</small></span>
            </label>)}
          </div>
        </fieldset>
        <fieldset>
          <legend>Durchlauf</legend>
          <label>Anzahl Aufgaben<select value={count} onChange={(event) => setCount(Number(event.target.value) as 5 | 10)}><option value={5}>5</option><option value={10}>10</option></select></label>
          <p className="muted">Ohne Zeitbegrenzung – Zeit zum Sprechen und Besprechen ist ausdrücklich vorgesehen.</p>
        </fieldset>
      </div>
      <button className="primary" disabled={busy || !canControl} onClick={() => start().catch((error) => setMessage(error.message))}>Stellenwertaufgaben starten</button>
      {message && <div className="alert info">{message}</div>}
    </section>;
  }

  const item = exercise.run.items[exercise.index];
  const lastAnswer = exercise.answers.at(-1);
  return <section className="stellenwert-addition">
    {canControl && exercise.phase !== "complete" && <ExerciseMenu>
      <button className="secondary small" disabled={busy} onClick={() => backToSettings().catch((error) => setMessage(error.message))}>← Zurück zu den Einstellungen</button>
      <button className="secondary small" disabled={busy} onClick={() => remix().catch((error) => setMessage(error.message))}>↻ Neu mischen</button>
    </ExerciseMenu>}
    <div className="exercise-progress">Aufgabe {Math.min(exercise.index + 1, exercise.run.items.length)} von {exercise.run.items.length} · {phaseOptions.find(({ id }) => id === exercise.run.settings.phase)?.label}</div>
    {exercise.phase === "complete" ? <><div className="reime-result"><strong>{exercise.result?.score ?? score} von {exercise.result?.maxScore ?? exercise.answers.length} Aufgaben vollständig richtig</strong><p>Der Durchlauf ist abgeschlossen.</p></div>{canControl && <CompletedRunActions onFinish={() => closeCompletedRun(false)} onRepeat={() => closeCompletedRun(true)} onError={setMessage} />}{exercise.phase === "complete" && <CompletedRunHistory key={exercise.runId} runId={exercise.runId} />}</> : item && <>
      <MaterialScene state={exercise} canAnswer={canAnswer} moveRod={moveRod} />
      <div className="place-value-legend"><span><i className="place-value-rod" /> 1 Stange = 10 Paletten</span><span><i className="place-value-cube" /> 1 Würfel = 1 Palette</span></div>
      {exercise.run.settings.phase === "move_tens" && exercise.phase !== "feedback" && <p className="material-hint">Bewege die Zehnerstangen des Lieferwagens per Klick oder Tastatur ins Lager. Die Einerwürfel bleiben liegen.</p>}

      <div className="calculation-paper">
        <h3 className="learning-text">Rechenzettel</h3>
        <div className="transcription-equation" aria-label="Sachsituation als Rechnung übertragen">
          <input aria-label="Paletten im Lager" type="number" inputMode="numeric" min={10} max={99} disabled={exercise.phase !== "transcribe" || busy || !canAnswer} value={exercise.draft.writtenFirstAddend} onChange={(event) => updateDraft({ writtenFirstAddend: event.target.value })} />
          <span>+</span>
          <input aria-label="Paletten auf dem Lieferwagen" type="number" inputMode="numeric" min={10} max={99} disabled={exercise.phase !== "transcribe" || busy || !canAnswer} value={exercise.draft.writtenSecondAddend} onChange={(event) => updateDraft({ writtenSecondAddend: event.target.value })} />
          <span>=</span><span className="calculation-blank">?</span>
        </div>
        {exercise.phase === "transcribe" && <button className="primary" disabled={busy || !canAnswer} onClick={checkTranscription}>Mit der Sachsituation vergleichen</button>}
        {exercise.phase === "transcribe" && exercise.draft.transcriptionNeedsReview && <div className="alert info" aria-live="polite">Vergleiche die beiden Zahlen noch einmal mit Lager und Lieferwagen.</div>}

        {(exercise.phase === "solve" || exercise.phase === "feedback") && <div className="step-calculation">
          <div><span>{item.firstAddend}</span><span>+</span><span>{item.tensPart}</span><span>=</span><input aria-label="Zwischenergebnis nach den Zehnern" type="number" inputMode="numeric" min={0} max={99} disabled={exercise.phase !== "solve" || busy || !canAnswer} value={exercise.draft.intermediateEntered} onChange={(event) => updateDraft({ intermediateEntered: event.target.value })} /></div>
          <div><span>{exercise.draft.intermediateEntered || "___"}</span><span>+</span><span>{item.onesPart}</span><span>=</span><input aria-label="Endergebnis" type="number" inputMode="numeric" min={0} max={99} disabled={exercise.phase !== "solve" || busy || !canAnswer} value={exercise.draft.resultEntered} onChange={(event) => updateDraft({ resultEntered: event.target.value })} /></div>
        </div>}
        {(exercise.phase === "solve" || exercise.phase === "feedback") && <SpeechHelp state={exercise} />}
        {exercise.phase === "solve" && <button className="primary" disabled={busy || !canAnswer || !exercise.draft.intermediateEntered || !exercise.draft.resultEntered} onClick={() => checkSolution().catch((error) => setMessage(error.message))}>Rechenweg prüfen</button>}
      </div>

      {exercise.phase === "feedback" && <div className={`answer-feedback ${lastAnswer?.correct ? "correct" : "wrong"}`} aria-live="polite">
        <strong>{lastAnswer?.correct ? "Richtig – beide Rechenschritte stimmen." : "Schau dir den vollständigen Rechenweg an:"}</strong>
        <p>{item.firstAddend} + {item.tensPart} = {item.intermediate}; {item.intermediate} + {item.onesPart} = {item.result}.</p>
        {!lastAnswer?.correct && lastAnswer?.resultEntered === item.result && lastAnswer.intermediateEntered !== item.intermediate && <p>Das Endergebnis stimmt, aber der geforderte Zwischenschritt noch nicht.</p>}
        {canControl && <button className="primary" disabled={busy} onClick={() => next().catch((error) => setMessage(error.message))}>{exercise.index + 1 >= exercise.run.items.length ? "Auswertung ansehen" : "Weiter zur nächsten Aufgabe"}</button>}
      </div>}
    </>}
    {message && <div className="alert info" aria-live="polite">{message}</div>}
  </section>;
}
